// components/ui/Toast/toast-provider.tsx
import * as React from "react"
import { Toast } from "./Toast"

export interface ToastData {
  id: string
  title?: string
  description?: string
  variant?: 'default' | 'destructive'
  duration?: number  // in milliseconds
}

interface ToastContextType {
  toasts: ToastData[]
  addToast: (toast: Omit<ToastData, "id">) => void
  removeToast: (id: string) => void
}

export const ToastContext = React.createContext<ToastContextType | undefined>(undefined)

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastData[]>([])

  const addToast = React.useCallback(
    ({ title, description, variant, duration = 3000 }: Omit<ToastData, "id">) => {
      const id = Math.random().toString(36).slice(2);
      setToasts((currentToasts) => [
        ...currentToasts,
        { id, title, description, variant, duration },
      ])

      // Auto remove after duration
      if (duration) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }
    },
    [ ]
  )

  const removeToast = React.useCallback((id: string) => {
    setToasts((currentToasts) =>
      currentToasts.filter((toast) => toast.id !== id)
    )
  }, [])

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 w-full max-w-md">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            title={toast.title}
            description={toast.description}
            variant={toast.variant}
            duration={toast.duration}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  )
}